import type {
  AssessmentPurpose,
  ProgramConfigInput,
  ProgramDomain,
  SubtypeVocabulary,
} from './schema';
import { subtypeLabelForTag } from './subtype-vocabulary';

// Typed seed data for the program-spine catalog — the source of truth that the
// contract test validates against `programConfigSchema`, and that `seed.sql`
// (executed by `pnpm db:seed`) mirrors.
//
// ⚠ PLACEHOLDER — clinical + product sign-off pending. The per-program `config`
// (instruments, meds_in_scope, vitals_of_interest, clinician_roles,
// care_track_rules, journey_template_ref, healthie_tag) is illustrative seed
// data demonstrating the shape, NOT a Pelago clinical spec. The table shapes are
// firm; these values are replaced once clinical/product confirm the real screener
// set, formulary, EHR tags, and care-track rules. Some `instruments` entries
// (phq2, gad2, readiness_ruler, assist) intentionally reference instruments not
// yet in ASSESSMENT_SEED — `instruments` is an open server-validated ref, not a
// DB FK, so the catalog grows independently.

export interface ProgramSeedRow {
  readonly key: string;
  readonly domain: ProgramDomain;
  readonly displayName: string;
  readonly config: ProgramConfigInput;
}

export const PROGRAM_SEED: readonly ProgramSeedRow[] = [
  {
    key: 'tobacco',
    domain: 'substance',
    displayName: 'Quit smoking / tobacco',
    config: {
      withdrawal_check: true,
      withdrawal_escalation_min_risk: 'urgent',
      priority: 5,
      usage_unit: 'cigarettes',
      min_age: 16,
      intake: { subtypes: ['cigarettes', 'ecigarettes', 'smokeless-tobacco'] },
      subtype_vocabulary: {
        answer_key: 'tobacco_subtype',
        unspecified_label: 'Tobacco or nicotine',
        entries: [
          { tag: 'cigarettes', label: 'Cigarettes', answer_values: ['cigarettes'] },
          {
            tag: 'ecigarettes',
            label: 'Vaping',
            prompt_label: 'Nicotine vaping',
            answer_values: ['vaping'],
            healthie_tag: 'eCigarettes',
          },
          { tag: 'smokeless-tobacco', label: 'Smokeless tobacco', answer_values: ['smokeless'] },
        ],
      },
      // FTND-derived time-to-first-use; positive = within 30 minutes of waking.
      taps2: {
        questions: [{ key: 'taps2_smoke_timing', positive_values: ['within_5', 'within_30'] }],
      },
      instruments: ['ftnd', 'phq2', 'gad2', 'readiness_ruler'],
      journey_template_ref: 'journey/tobacco-quit-v1',
      checkin_schema: 'substance-retrospective-quantified',
      initial_review: {
        presentation_id: 'quantified_check_ins_v1',
        quantity_limit: 99,
        copy_ref: 'quantified_substance_v1',
      },
      meds_in_scope: ['varenicline', 'bupropion', 'nrt_patch', 'nrt_gum', 'nrt_lozenge'],
      vitals_of_interest: ['co_breath_ppm', 'cotinine'],
      healthie_tag: 'cigarettes',
      clinician_roles: ['care_specialist', 'prescriber'],
      care_track_rules: { default: 'ai_led', escalate_if: ['ftnd_high', 'comorbid_mood_severe'] },
      care: {
        welcome_call_type: 'welcome_call_tobacco',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: false,
      },
    },
  },
  {
    key: 'alcohol',
    domain: 'substance',
    displayName: 'Alcohol',
    config: {
      withdrawal_check: true,
      priority: 3,
      usage_unit: 'drinks',
      min_age: 16,
      // Concern-from-others item; the drink-count and control items are
      // inferred from baseline rather than asked.
      taps2: {
        questions: [{ key: 'taps2_alcohol_concern', positive_values: ['yes'] }],
      },
      instruments: ['audit', 'phq9', 'gad7', 'readiness_ruler'],
      journey_template_ref: 'journey/alcohol-v1',
      checkin_schema: 'substance-retrospective-quantified',
      initial_review: {
        presentation_id: 'quantified_check_ins_v1',
        quantity_limit: 99,
        copy_ref: 'alcohol_v1',
      },
      meds_in_scope: ['naltrexone', 'acamprosate', 'disulfiram'],
      vitals_of_interest: ['bac', 'peth', 'ggt'],
      healthie_tag: 'alcohol',
      clinician_roles: ['care_specialist', 'counselor', 'prescriber', 'physician'],
      care_track_rules: {
        default: 'ai_led',
        escalate_if: ['withdrawal_risk', 'audit_severe', 'comorbid_mood_severe'],
      },
      care: {
        welcome_call_type: 'welcome_call_substance',
        licence_readiness: 'aud',
        medication_eligible: true,
        followed_by_treatment_plan: true,
      },
    },
  },
  {
    key: 'opioid',
    domain: 'substance',
    displayName: 'Opioids',
    config: {
      withdrawal_check: true,
      priority: 1,
      usage_unit: 'doses',
      min_age: 16,
      // Misuse and concern-from-others items; the control item is inferred
      // from baseline rather than asked.
      taps2: {
        questions: [
          { key: 'taps2_opioid_misuse', positive_values: ['yes'] },
          { key: 'taps2_opioid_concern', positive_values: ['yes'] },
        ],
      },
      instruments: ['dast10', 'cows', 'phq9', 'gad7'],
      journey_template_ref: 'journey/opioid-mat-v1',
      checkin_schema: 'behavioral-realtime-episodic',
      initial_review: {
        presentation_id: 'quantified_check_ins_v1',
        quantity_limit: 99,
        copy_ref: 'yes_no_substance_v1',
      },
      meds_in_scope: ['buprenorphine_naloxone', 'naltrexone_xr', 'methadone'],
      vitals_of_interest: ['udt', 'cows_score'],
      healthie_tag: 'opioids',
      clinician_roles: ['prescriber', 'physician', 'counselor', 'coordinator', 'care_specialist'],
      care_track_rules: {
        default: 'human_led',
        escalate_if: ['withdrawal_active', 'overdose_risk'],
      },
      care: {
        welcome_call_type: 'welcome_call_substance',
        licence_readiness: 'oud',
        medication_eligible: true,
        followed_by_treatment_plan: true,
      },
    },
  },
  {
    key: 'cannabis',
    domain: 'substance',
    displayName: 'Cannabis',
    config: {
      withdrawal_check: true,
      withdrawal_escalation_min_risk: 'urgent',
      priority: 4,
      usage_unit: 'sessions',
      min_age: 16,
      // Concern-from-others item; the frequency and control items are
      // inferred from baseline rather than asked.
      taps2: {
        questions: [{ key: 'taps2_cannabis_concern', positive_values: ['yes'] }],
      },
      instruments: ['cudit_r', 'phq9', 'gad7', 'readiness_ruler'],
      journey_template_ref: 'journey/cannabis-v1',
      checkin_schema: 'substance-retrospective-quantified',
      initial_review: {
        presentation_id: 'quantified_check_ins_v1',
        quantity_limit: 99,
        copy_ref: 'quantified_substance_v1',
      },
      meds_in_scope: [],
      vitals_of_interest: ['udt_thc'],
      healthie_tag: 'cannabis',
      clinician_roles: ['care_specialist', 'counselor'],
      care_track_rules: { default: 'ai_led', escalate_if: ['comorbid_mood_severe'] },
      care: {
        welcome_call_type: 'welcome_call_substance',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: true,
      },
    },
  },
  {
    key: 'stimulants',
    domain: 'substance',
    displayName: 'Stimulants',
    config: {
      withdrawal_check: true,
      priority: 2,
      usage_unit: 'doses',
      min_age: 16,
      instruments: ['dast10', 'assist', 'phq9', 'gad7'],
      journey_template_ref: 'journey/stimulants-v1',
      checkin_schema: 'behavioral-realtime-episodic',
      initial_review: {
        presentation_id: 'quantified_check_ins_v1',
        quantity_limit: 99,
        copy_ref: 'yes_no_substance_v1',
      },
      meds_in_scope: [],
      vitals_of_interest: ['udt'],
      healthie_tag: 'stimulants',
      clinician_roles: ['care_specialist', 'counselor'],
      care_track_rules: {
        default: 'ai_led',
        escalate_if: ['psychosis_risk', 'comorbid_mood_severe'],
      },
      care: {
        welcome_call_type: 'welcome_call_substance',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: true,
      },
    },
  },
  {
    key: 'gambling',
    domain: 'behavioral',
    displayName: 'Gambling',
    config: {
      priority: 6,
      min_age: 16,
      withdrawal_escalation_min_risk: 'urgent',
      instruments: ['pgsi', 'phq9', 'gad7'],
      journey_template_ref: 'journey/gambling-v1',
      checkin_schema: 'behavioral-realtime-episodic',
      meds_in_scope: [],
      vitals_of_interest: [],
      healthie_tag: 'gambling',
      clinician_roles: ['care_specialist', 'counselor'],
      care_track_rules: { default: 'ai_led', escalate_if: ['financial_crisis', 'suicidality'] },
      care: {
        welcome_call_type: 'welcome_call_behavioral',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: false,
      },
    },
  },
  {
    key: 'gaming',
    domain: 'behavioral',
    displayName: 'Gaming',
    config: {
      priority: 7,
      min_age: 16,
      withdrawal_escalation_min_risk: 'urgent',
      instruments: ['igds9_sf', 'phq9', 'gad7'],
      journey_template_ref: 'journey/gaming-v1',
      checkin_schema: 'behavioral-realtime-episodic',
      meds_in_scope: [],
      vitals_of_interest: [],
      healthie_tag: 'gaming',
      clinician_roles: ['care_specialist', 'counselor'],
      care_track_rules: {
        default: 'ai_led',
        escalate_if: ['functional_impairment_severe', 'comorbid_mood_severe'],
      },
      care: {
        welcome_call_type: 'welcome_call_behavioral',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: false,
      },
    },
  },
  {
    key: 'mood',
    domain: 'mood',
    displayName: 'Mood / depression & anxiety',
    config: {
      priority: 8,
      min_age: 16,
      instruments: ['phq9', 'gad7', 'phq2', 'gad2'],
      journey_template_ref: 'journey/mood-v1',
      checkin_schema: 'mood-daily-rating',
      meds_in_scope: ['sertraline', 'escitalopram', 'bupropion'],
      vitals_of_interest: ['phq9_score', 'gad7_score'],
      healthie_tag: 'mood',
      clinician_roles: ['counselor', 'prescriber', 'coordinator', 'care_specialist'],
      care_track_rules: { default: 'human_led', escalate_if: ['phq9_severe', 'suicidality'] },
      care: {
        welcome_call_type: 'welcome_call_behavioral',
        licence_readiness: null,
        medication_eligible: false,
        followed_by_treatment_plan: false,
      },
    },
  },
];

export interface AssessmentSeedRow {
  readonly key: string;
  readonly version: number;
  readonly purpose: AssessmentPurpose;
}

// The screeners the launch catalog references. `config` is a placeholder empty
// object pending the real question/scoring spec (clinical sign-off).
export const ASSESSMENT_SEED: readonly AssessmentSeedRow[] = [
  { key: 'phq9', version: 1, purpose: 'in_program' },
  { key: 'gad7', version: 1, purpose: 'in_program' },
  { key: 'audit', version: 1, purpose: 'in_program' },
  { key: 'ftnd', version: 1, purpose: 'in_program' },
  { key: 'cudit_r', version: 1, purpose: 'in_program' },
  { key: 'pgsi', version: 1, purpose: 'in_program' },
  { key: 'igds9_sf', version: 1, purpose: 'in_program' },
  { key: 'dast10', version: 1, purpose: 'in_program' },
  { key: 'cows', version: 1, purpose: 'in_program' },
];

// Catalog display names by key, and the clinical ordering the catalog declares
// (`config.priority`, ascending — opioid first, mood last). Consumers that offer
// programmes to a human — the Atlas member-list filter, the per-icon tooltip —
// read these instead of title-casing the key, so `opioid` reads "Opioids" and
// `stimulants` reads "Stimulants" rather than two indistinguishable pill icons.
export const PROGRAM_DISPLAY_NAME: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(PROGRAM_SEED.map((p) => [p.key, p.displayName])),
);

/** Catalog display name for a key, falling back to the key itself when unknown. */
export function programDisplayName(key: string): string {
  return PROGRAM_DISPLAY_NAME[key] ?? key;
}

// Short labels for compact programme tiles where the catalog display name wraps
// (for example tobacco's full intake name). Unlisted keys use `programDisplayName`.
export const PROGRAM_TILE_SHORT_NAME: Readonly<Partial<Record<string, string>>> = Object.freeze({
  tobacco: 'Tobacco',
});

/** Tile-safe programme label — short when the catalog name is too long for a grid cell. */
export function programTileShortName(key: string): string {
  return PROGRAM_TILE_SHORT_NAME[key] ?? programDisplayName(key);
}

/**
 * A programme's sub-type vocabulary from the seed catalog, for readers that hold
 * no database row (shared code, Atlas). API readers parse the same key from
 * `program_definitions.config` instead.
 */
export function programSubtypeVocabulary(key: string): SubtypeVocabulary | undefined {
  return PROGRAM_SEED.find((p) => p.key === key)?.config.subtype_vocabulary;
}

/** The label a reader shows for one of a programme's sub-type tags; undefined when unknown. */
export function programSubtypeLabel(key: string, tag: string): string | undefined {
  return subtypeLabelForTag(programSubtypeVocabulary(key), tag);
}

/**
 * The countable noun each programme's use is measured in (`drinks`,
 * `cigarettes`, …), from the catalog's `usage_unit`. Programmes without one
 * (gambling, gaming, mood) are absent.
 */
const PROGRAM_USAGE_UNIT: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    PROGRAM_SEED.flatMap((p) => (p.config.usage_unit ? [[p.key, p.config.usage_unit]] : [])),
  ),
);

/** Every distinct usage unit the catalog declares. */
export const PROGRAM_USAGE_UNITS: readonly string[] = Object.freeze([
  ...new Set(Object.values(PROGRAM_USAGE_UNIT)),
]);

/**
 * Catalog keys whose check-in records something countable — every programme
 * except the rating-shaped ones.
 *
 * The discriminator is the CHECK-IN SCHEMA, not a list of names and not the
 * domain: a `mood-daily-rating` schema has no quantity to total and no
 * free-versus-quantity choice to make, and that is the property the consumers
 * actually depend on. `domain` happens to agree today, but a future rating-shaped
 * programme filed under another domain would slip past a domain test while still
 * having nothing to count. Surfaces that name what a member tracked
 * — the past-day count card and its free-day counterpart — read this instead of
 * excluding `mood` by hand, so a future rating-shaped programme is excluded by
 * the rule rather than by an edit at every call site.
 *
 * Derived, so a programme added to `PROGRAM_SEED` joins it automatically and the
 * copy guards that consume it fail until that programme's vocabulary lands.
 */
export const TRACKED_PROGRAM_KEYS: readonly string[] = Object.freeze(
  PROGRAM_SEED.filter((p) => p.config.checkin_schema !== 'mood-daily-rating').map((p) => p.key),
);

/** Catalog keys in the clinical priority order the catalog declares. */
export const PROGRAM_KEYS_BY_PRIORITY: readonly string[] = Object.freeze(
  [...PROGRAM_SEED]
    .sort(
      (a, b) =>
        (a.config.priority ?? Number.MAX_SAFE_INTEGER) -
        (b.config.priority ?? Number.MAX_SAFE_INTEGER),
    )
    .map((p) => p.key),
);
